import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Icons,
  iconSize,
  MenuItem,
  Stack,
  TagChip,
  TextField,
  Tooltip,
  Typography,
  palette,
  useSnackbar,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_SNIPPET_BODY,
  MAX_SNIPPET_TITLE,
  SNIPPET_CONTEXTS,
  productivityEnabledFor,
  type NoteSnippet,
  type SnippetContext,
  type SnippetScope,
} from '@munifocus/contracts-gov';
import {
  useCreateNoteSnippet,
  useDeleteNoteSnippet,
  useNoteSnippets,
  useProductivitySettings,
  useUpdateNoteSnippet,
} from '../../lib/productivity-api';
import { apiErrorMessage } from '../../lib/mutation-toast';
import { useConfirmAction } from '../../lib/useConfirmAction';
import { useOptionalScope } from '../../providers/ScopeProvider';
import { SettingsSection } from './SettingsSection';

interface Draft {
  id: string | null;
  title: string;
  bodyEn: string;
  bodyFr: string;
  context: SnippetContext;
  scope: SnippetScope;
}

const EMPTY: Draft = {
  id: null,
  title: '',
  bodyEn: '',
  bodyFr: '',
  context: 'any',
  scope: 'personal',
};

/**
 * Note snippets: canned text for file notes and letters, written in English and
 * French side by side so neither language is the afterthought.
 *
 * Personal snippets are the officer's own. Team snippets belong to the
 * municipality in the scope switcher and are offered only to an account that
 * manages file teams there — the API refuses anyone else regardless.
 */
export function SnippetsSection() {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const confirm = useConfirmAction();
  const scope = useOptionalScope();
  const settings = useProductivitySettings();
  const snippets = useNoteSnippets();
  const create = useCreateNoteSnippet();
  const update = useUpdateNoteSnippet();
  const remove = useDeleteNoteSnippet();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const teamLg = scope?.active?.kind === 'local_government' ? scope.active : null;
  const canTeam =
    !!teamLg &&
    !!scope?.can('file:team:manage') &&
    productivityEnabledFor(settings.data, teamLg.id);

  const titleError = submitted && !draft?.title.trim();
  const bodyError = submitted && !draft?.bodyEn.trim() && !draft?.bodyFr.trim();

  const onSave = () => {
    if (!draft) return;
    setSubmitted(true);
    if (!draft.title.trim() || (!draft.bodyEn.trim() && !draft.bodyFr.trim())) return;
    const body = {
      title: draft.title.trim(),
      bodyEn: draft.bodyEn.trim() || null,
      bodyFr: draft.bodyFr.trim() || null,
      context: draft.context,
    };
    const done = {
      onSuccess: () => {
        snackbar.success(t('gov.snippets.saved'));
        setDraft(null);
        setSubmitted(false);
      },
      onError: (err: unknown) => snackbar.error(apiErrorMessage(err, t('gov.snippets.saveError'))),
    };
    if (draft.id) update.mutate({ id: draft.id, ...body }, done);
    else
      create.mutate(
        {
          ...body,
          scope: draft.scope,
          localGovernmentId: draft.scope === 'team' ? (teamLg?.id ?? null) : null,
        },
        done,
      );
  };

  const onDelete = async (snippet: NoteSnippet) => {
    const ok = await confirm({
      title: t('gov.snippets.deleteTitle'),
      body: t('gov.snippets.deleteBody', { title: snippet.title }),
      confirmLabel: t('common.actions.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    remove.mutate(snippet.id, {
      onSuccess: () => snackbar.success(t('gov.snippets.deleted')),
      onError: (err) => snackbar.error(apiErrorMessage(err, t('gov.snippets.saveError'))),
    });
  };

  const rows = snippets.data ?? [];
  const saving = create.isPending || update.isPending;

  return (
    <Box id="snippets">
      <SettingsSection
        icon={Icons.TextQuote}
        title={t('gov.snippets.title')}
        description={t('gov.snippets.description')}
      >
        {snippets.isLoading && <CircularProgress size={24} />}
        {snippets.isError && <Alert severity="error">{t('gov.snippets.loadError')}</Alert>}
        {snippets.data && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('gov.snippets.empty')}
          </Typography>
        )}
        {rows.length > 0 && (
          <Box component="ul" sx={{ m: 0, p: 0, mb: 1.5 }}>
            {rows.map((s) => (
              <Stack
                key={s.id}
                component="li"
                direction="row"
                spacing={1}
                sx={{
                  listStyle: 'none',
                  py: 1,
                  alignItems: 'center',
                  borderTop: `1px solid ${palette.border.line}`,
                }}
              >
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {s.title}
                    </Typography>
                    <TagChip label={t(`gov.snippets.scope.${s.scope}`)} />
                    <TagChip label={t(`gov.snippets.context.${s.context}`)} />
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {[
                      s.bodyEn && t('gov.snippets.hasEnglish'),
                      s.bodyFr && t('gov.snippets.hasFrench'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    {s.scope === 'team' && s.ownerName ? ` · ${s.ownerName}` : ''}
                  </Typography>
                </Box>
                {s.canEdit && (
                  <>
                    <Tooltip title={t('common.actions.edit')}>
                      <IconButton
                        size="small"
                        aria-label={t('gov.snippets.editNamed', { title: s.title })}
                        onClick={() => {
                          setSubmitted(false);
                          setDraft({
                            id: s.id,
                            title: s.title,
                            bodyEn: s.bodyEn ?? '',
                            bodyFr: s.bodyFr ?? '',
                            context: s.context,
                            scope: s.scope,
                          });
                        }}
                      >
                        <Icons.Pencil size={iconSize.sm} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.actions.delete')}>
                      <IconButton
                        size="small"
                        aria-label={t('gov.snippets.deleteNamed', { title: s.title })}
                        onClick={() => void onDelete(s)}
                      >
                        <Icons.Trash2 size={iconSize.sm} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            ))}
          </Box>
        )}
        <Button
          size="small"
          startIcon={<Icons.Plus size={iconSize.sm} />}
          onClick={() => {
            setSubmitted(false);
            setDraft({ ...EMPTY });
          }}
        >
          {t('gov.snippets.add')}
        </Button>
      </SettingsSection>

      <Dialog open={!!draft} onClose={() => setDraft(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {draft?.id ? t('gov.snippets.editTitle') : t('gov.snippets.addTitle')}
        </DialogTitle>
        <DialogContent>
          {draft && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label={t('gov.snippets.fieldTitle')}
                value={draft.title}
                required
                error={titleError}
                helperText={titleError ? t('gov.snippets.titleRequired') : undefined}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                slotProps={{ htmlInput: { maxLength: MAX_SNIPPET_TITLE } }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  label={t('gov.snippets.fieldContext')}
                  value={draft.context}
                  onChange={(e) =>
                    setDraft({ ...draft, context: e.target.value as SnippetContext })
                  }
                >
                  {SNIPPET_CONTEXTS.map((c) => (
                    <MenuItem key={c} value={c}>
                      {t(`gov.snippets.context.${c}`)}
                    </MenuItem>
                  ))}
                </TextField>
                {!draft.id && canTeam && (
                  <TextField
                    select
                    fullWidth
                    label={t('gov.snippets.fieldScope')}
                    value={draft.scope}
                    onChange={(e) => setDraft({ ...draft, scope: e.target.value as SnippetScope })}
                    helperText={
                      draft.scope === 'team' && teamLg
                        ? t('gov.snippets.teamHint', { place: teamLg.name })
                        : undefined
                    }
                  >
                    <MenuItem value="personal">{t('gov.snippets.scope.personal')}</MenuItem>
                    <MenuItem value="team">{t('gov.snippets.scope.team')}</MenuItem>
                  </TextField>
                )}
              </Stack>
              {/* Side by side from `md`: equal weight, and the longer of the two
                  languages sets the height. */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label={t('gov.snippets.fieldBodyEn')}
                  value={draft.bodyEn}
                  error={bodyError}
                  onChange={(e) => setDraft({ ...draft, bodyEn: e.target.value })}
                  slotProps={{ htmlInput: { maxLength: MAX_SNIPPET_BODY, lang: 'en' } }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label={t('gov.snippets.fieldBodyFr')}
                  value={draft.bodyFr}
                  error={bodyError}
                  onChange={(e) => setDraft({ ...draft, bodyFr: e.target.value })}
                  slotProps={{ htmlInput: { maxLength: MAX_SNIPPET_BODY, lang: 'fr' } }}
                />
              </Stack>
              {bodyError && (
                <Typography variant="caption" color="error">
                  {t('gov.snippets.bodyRequired')}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)} disabled={saving}>
            {t('common.actions.cancel')}
          </Button>
          <Button variant="contained" onClick={onSave} disabled={saving}>
            {saving ? t('common.actions.saving') : t('common.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
